import React from 'react';
import { Link } from 'react-router-dom';
import {
  GraduationCap,
  MapPin,
  Phone,
  Mail,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  ArrowRight
} from 'lucide-react';

const Footer = () => {
  const quickLinks = [
    { name: 'About Us', href: '/about' },
    { name: 'Admissions', href: '/admissions' },
    { name: 'Academics', href: '/academics' },
    { name: 'TAG\'s Mission', href: '/about/mission' },
    { name: 'Staff List', href: '/about/staff' },
    { name: 'Virtual Tour', href: '/about/virtual-tour' },
  ];

  const resources = [
    { name: 'Parent\'s Corner', href: '/resources/parents' },
    { name: 'Monthly Newsletters', href: '/resources/newsletters' },
    { name: 'DOE Resources', href: '/resources/doe' },
    { name: 'TAG PTA', href: '/community/pta' },
    { name: 'NYC Schools Account', href: 'https://mystudent.nyc/' },
  ];

  const socialLinks = [
    { icon: Facebook, href: '/coming-soon', label: 'Facebook' },
    { icon: Twitter, href: '/coming-soon', label: 'Twitter' },
    { icon: Instagram, href: '/coming-soon', label: 'Instagram' },
    { icon: Linkedin, href: '/coming-soon', label: 'LinkedIn' },
  ];

  return (
    <footer className="bg-slate-900 text-white">
      {/* Main Footer */}
      <div className="container-padding section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* School Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-primary-500 p-3 rounded-xl">
                <GraduationCap className="h-8 w-8 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold">Talented and Gifted School for Young Scholars</p>
                <p className="text-slate-400">Where Children Come First</p>
              </div>
            </div>

            <p className="text-slate-300 mb-6 leading-relaxed max-w-md">
              At TAG School, faculties, parents and students work together to prepare all students
              to excel in life and develop to their full potential, both intellectually and socially.
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <MapPin className="h-5 w-5 text-accent-400" />
                <span className="text-slate-300">240 East 109 Street, Manhattan, NY 10029</span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-accent-400" />
                <div className="text-slate-300">
                  <div>Phone: 212-860-6003</div>
                  <div>Fax: 212-831-1842</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-accent-400" />
                <span className="text-slate-300">info@schoolname.edu</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex space-x-4 mt-6">
              {socialLinks.map((social, index) => (
                <Link
                  key={index}
                  to={social.href}
                  aria-label={social.label}
                  className="bg-slate-800 hover:bg-primary-500 p-3 rounded-lg transition-all duration-300 hover:scale-110"
                >
                  <social.icon aria-hidden="true" className="h-5 w-5" />
                </Link>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <nav aria-label="Quick Links">
            <p className="text-lg font-semibold mb-6">Quick Links</p>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    className="text-slate-300 hover:text-white transition-colors duration-300 flex items-center group"
                  >
                    <ArrowRight aria-hidden="true" className="h-4 w-4 mr-2 group-hover:translate-x-1 transition-transform" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Resources */}
          <div>
            <nav aria-label="Resources">
              <p className="text-lg font-semibold mb-6">Resources</p>
              <ul className="space-y-3">
                {resources.map((resource, index) => (
                  <li key={index}>
                    {resource.href.startsWith('http') ? (
                      <a
                        href={resource.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-300 hover:text-white transition-colors duration-300 flex items-center group"
                      >
                        <ArrowRight aria-hidden="true" className="h-4 w-4 mr-2 group-hover:translate-x-1 transition-transform" />
                        {resource.name}
                      </a>
                    ) : (
                      <Link
                        to={resource.href}
                        className="text-slate-300 hover:text-white transition-colors duration-300 flex items-center group"
                      >
                        <ArrowRight aria-hidden="true" className="h-4 w-4 mr-2 group-hover:translate-x-1 transition-transform" />
                        {resource.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </nav>

            {/* Newsletter Signup */}
            <div className="mt-8">
              <p className="font-semibold mb-3">Stay Updated</p>
              <form onSubmit={(e) => e.preventDefault()} role="search">
                <div className="flex">
                  <input
                    type="email"
                    placeholder="Your email"
                    aria-label="Email address for newsletter signup"
                    className="flex-1 px-4 py-2 bg-slate-800 border border-slate-700 rounded-l-lg focus:outline-none focus:border-primary-500 text-white placeholder-slate-400"
                  />
                  <button
                    type="submit"
                    aria-label="Subscribe to newsletter"
                    className="bg-primary-500 hover:bg-primary-600 px-4 py-2 rounded-r-lg transition-colors"
                  >
                    <ArrowRight aria-hidden="true" className="h-5 w-5" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="container-padding py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 text-slate-400 mb-4 md:mb-0">
              <span>&copy; 2024 Talented and Gifted School for Young Scholars. All rights reserved.</span>
            </div>

            <div className="flex items-center space-x-6 text-sm text-slate-400">
              <Link to="/coming-soon" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/coming-soon" className="hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/coming-soon" className="hover:text-white transition-colors">Accessibility</Link>
            </div>
          </div>

          {/* NexSav Credit */}
          <div className="mt-4 pt-4 border-t border-white/5">
            <a
              href="https://nexsav.net"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 text-white/60 hover:text-white/90 transition-all duration-300 group"
            >
              <img
                src="https://nexsav.net/static/media/logo.7ef8f9a8ca3ec946bd31.png"
                alt="NexSav Logo"
                className="h-5 opacity-60 group-hover:opacity-90 transition-opacity duration-300"
              />
              <span className="text-xs font-medium tracking-wide">
                Website designed & developed by <span className="font-semibold">NexSav</span>
              </span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;